// Data-model market codes. ISO 3166-1 alpha-2.
//
//   MARKETS    — country codes that have ever been valid in the data model;
//                used by shared Zod schemas + API filter enums for backwards
//                compat (LV was purged via migration 0006 but stays so
//                historical payloads validate).
//
//   UI_MARKETS — the live launch surface: ALL 27 target markets.
//                (Until 2026-07-21 this line named a count roughly half that,
//                while the array below already held 27. A stale count in a
//                comment is not harmless — it was read as evidence that panels
//                were still market-gated when they were not, and produced a
//                confidently wrong answer about what had shipped. If you change
//                the array, change this number. A test asserts it.)
//                Screener filters, the workspace market picker, /markets and
//                Company Overview pickers offer this set.
export const MARKETS = [
  'EE',
  'DK',
  'NO',
  'BE',
  'SE',
  'NL',
  'LV',
  'GB',
  'FR',
  'DE',
  'FI',
  'RO',
  'IT',
  'IE',
  'ES',
  'CZ',
  'CH',
  'PL',
  'GR',
  'SI',
  'LT',
  'HR',
  'HU',
  'AT',
  'PT',
  'SK',
  'BG',
] as const;
export type MarketCode = (typeof MARKETS)[number];

export const UI_MARKETS = [
  'GB',
  'FR',
  'DE',
  'AT',
  'PT',
  'ES',
  'IT',
  'NL',
  'BE',
  'CH',
  'SE',
  'NO',
  'DK',
  'FI',
  'PL',
  'CZ',
  'SK',
  'RO',
  'BG',
  'GR',
  'SI',
  'HR',
  'HU',
  'LT',
  'LV',
  'IE',
  'EE',
] as const satisfies readonly MarketCode[];
export type UiMarketCode = (typeof UI_MARKETS)[number];

export const MARKET_NAMES: Record<MarketCode, string> = {
  EE: 'Estonia',
  DK: 'Denmark',
  NO: 'Norway',
  BE: 'Belgium',
  SE: 'Sweden',
  NL: 'Netherlands',
  LV: 'Latvia',
  GB: 'United Kingdom',
  FR: 'France',
  DE: 'Germany',
  FI: 'Finland',
  RO: 'Romania',
  IT: 'Italy',
  IE: 'Ireland',
  ES: 'Spain',
  CZ: 'Czechia',
  CH: 'Switzerland',
  PL: 'Poland',
  GR: 'Greece',
  SI: 'Slovenia',
  LT: 'Lithuania',
  HR: 'Croatia',
  HU: 'Hungary',
  AT: 'Austria',
  PT: 'Portugal',
  SK: 'Slovakia',
  BG: 'Bulgaria',
};

// The key the census_* tables store a market under: MARKET_NAMES, except GB
// ('UK', not 'United Kingdom').
//
// Defined ONCE because two places must agree on it or a catchment silently
// profiles the wrong country: /census reads census_area by this key, and
// /bolt-on resolves a dropped pin's market from the same table. They disagreed
// until 2026-08-02 — the Catchment panel guessed the market from longitude
// ('FR' east of 2°E, else 'GB'), a two-market leftover that sent every Romanian
// catchment to France's IRIS tables (blank), and every Belgian/Swiss catchment
// near the French border to REAL French demographics (silently wrong).
export const CENSUS_MARKET_NAME = Object.fromEntries(
  MARKETS.map((c) => [c, c === 'GB' ? 'UK' : MARKET_NAMES[c]]),
) as Record<MarketCode, string>;

const CENSUS_NAME_TO_MARKET = new Map<string, MarketCode>(
  MARKETS.map((c) => [CENSUS_MARKET_NAME[c], c]),
);

/** Census table key ('Romania', 'UK') → market code. null when unrecognised. */
export function marketFromCensusName(name: string): MarketCode | null {
  return CENSUS_NAME_TO_MARKET.get(name) ?? null;
}

/** Narrow an arbitrary string (e.g. entities.country_code) to a MarketCode. */
export function asMarketCode(code: string | null | undefined): MarketCode | null {
  return code != null && (MARKETS as readonly string[]).includes(code)
    ? (code as MarketCode)
    : null;
}

// The 11 TARGET markets for the buy-and-build / Market Opportunity surface.
//
// This is the STRATEGY roster (what we aim to cover), and is deliberately
// distinct from MARKETS above (the data-model `country_code` enum). MARKETS
// still carries early-phase codes that are NOT targets (EE, LV) and lacks four
// that ARE (DE, ES, IT, IE — never modelled in code yet). Source:
// Market_Opportunity_Panel_Handover.md (the region roster + the register
// classification, §5 trust layer).
//
//   register = 'complete' — national company register, coverage by
//     construction, no masking (UK, FR, DE, NL, BE, NO, DK, SE).
//   register = 'capacity' — no clean register; needs the external-count
//     coverage layer, cells below threshold masked "coverage-limited"
//     (ES, IT, IE — ISTAT/INE/CRO official counts).
//
// Order is the canonical roster: complete-register markets first, capacity
// markets last (so the coverage layer clusters at the right edge of the
// cross-market matrix).
export const TARGET_MARKETS = [
  { code: 'GB', name: 'United Kingdom', register: 'complete' },
  { code: 'FR', name: 'France', register: 'complete' },
  { code: 'DE', name: 'Germany', register: 'complete' },
  { code: 'NL', name: 'Netherlands', register: 'complete' },
  { code: 'BE', name: 'Belgium', register: 'complete' },
  { code: 'NO', name: 'Norway', register: 'complete' },
  { code: 'DK', name: 'Denmark', register: 'complete' },
  { code: 'SE', name: 'Sweden', register: 'complete' },
  { code: 'ES', name: 'Spain', register: 'capacity' },
  { code: 'IT', name: 'Italy', register: 'capacity' },
  { code: 'IE', name: 'Ireland', register: 'capacity' },
] as const satisfies ReadonlyArray<{
  code: string;
  name: string;
  register: 'complete' | 'capacity';
}>;

export type TargetMarketCode = (typeof TARGET_MARKETS)[number]['code'];
export const TARGET_MARKET_CODES = TARGET_MARKETS.map((m) => m.code) as readonly TargetMarketCode[];

/** Target markets that also exist in the data-model `country_code` enum, so
 *  they are safe to pass to a SQL `country_code IN (...)` filter (DE/ES/IT/IE
 *  aren't modelled yet and would raise an invalid-enum error). The Market
 *  Opportunity rollup queries this set; the panel frames ALL 11 as columns and
 *  renders the rest as the coverage layer. */
export const TARGET_MARKETS_IN_DATA_MODEL = TARGET_MARKET_CODES.filter((c) =>
  (MARKETS as readonly string[]).includes(c),
) as readonly TargetMarketCode[];

// Primary language per market — used for NLP routing, not as a UI default.
// Belgium has three; Flemish (NL) is the largest filing language.
// UK has multiple but English is the universal filing language for
// Companies House / NHS / regulators.
export const MARKET_PRIMARY_LANGUAGE: Record<MarketCode, string> = {
  EE: 'et',
  DK: 'da',
  NO: 'no',
  BE: 'nl',
  SE: 'sv',
  NL: 'nl',
  LV: 'lv',
  GB: 'en',
  FR: 'fr',
  DE: 'de',
  FI: 'fi',
  RO: 'ro',
  IT: 'it',
  IE: 'en',
  ES: 'es',
  CZ: 'cs',
  CH: 'de',
  PL: 'pl',
  GR: 'el',
  SI: 'sl',
  LT: 'lt',
  HR: 'hr',
  HU: 'hu',
  AT: 'de',
  PT: 'pt',
  SK: 'sk',
  BG: 'bg',
};

// The currency a market's stored monetary values are denominated in, for all 27.
//
// ⚠️ THIS EXISTS TO KILL A `?? 'GBP'` FALLBACK. `recomputeMetrics` converts
// turnover to EUR using the filing currency of the entity's latest financial
// period. Where an entity has NO period row — which is the normal case for a
// MODELLED market, not an edge case — that currency is null and the code fell
// back to 'GBP', a leftover from when the path was GB-only. Measured 2026-08-09:
// **511,163 non-GB rows with revenue have no filing currency** and would be
// priced as pounds — ES 180,132 (100% of the market), CZ 36,970 (100%),
// PL 40,892 (95%), IT 37,033 (99%), SI and AT 100%. The recompute candidate
// query deliberately includes period-less rows (`latest.entity_id IS NOT NULL
// OR enm.entity_id IS NOT NULL`), so nothing upstream filters them out.
//
// It had not fired only because `recompute.normalised_metrics` has not
// completed since 2026-08-04 — the failure was masking the bug.
//
// Mirrors NATIVE_CURRENCY_BY_COUNTRY in apps/web/src/currency.ts (which is
// client-side and unreachable from a service) and the per-market
// `filing_currency` literals in merge-staging.ts. Keep all three in step.
export const NATIVE_CURRENCY_BY_MARKET: Record<string, string> = {
  AT: 'EUR',
  BE: 'EUR',
  BG: 'BGN',
  CH: 'CHF',
  CZ: 'CZK',
  DE: 'EUR',
  DK: 'DKK',
  EE: 'EUR',
  ES: 'EUR',
  FI: 'EUR',
  FR: 'EUR',
  GB: 'GBP',
  GR: 'EUR',
  HR: 'EUR',
  HU: 'HUF',
  IE: 'EUR',
  IT: 'EUR',
  LT: 'EUR',
  LV: 'EUR',
  NL: 'EUR',
  NO: 'NOK',
  PL: 'PLN',
  PT: 'EUR',
  RO: 'RON',
  SE: 'SEK',
  SI: 'EUR',
  SK: 'EUR',
};

/** The filing currency to assume when an entity has no period row to read one
 *  from. Returns null for an unknown market so the caller can decide — never
 *  guess a currency for a market we do not serve. */
export function nativeCurrencyForMarket(market: string | null | undefined): string | null {
  if (!market) return null;
  return NATIVE_CURRENCY_BY_MARKET[market.toUpperCase()] ?? null;
}

// ── Full launch coverage — the 27 countries the platform will cover ──────────
// Authoritative coverage roster (user-confirmed 2026-07-03; NO Liechtenstein).
// Supersedes the 11-market TARGET_MARKETS strategy roster. Kept ADDITIVE: MARKETS
// (the country_code enum), TARGET_MARKETS, and UI_MARKETS are unchanged so
// existing panels + DB enums are undisturbed until the 27-market ingest lands
// them. Register classification (complete/capacity) per new market is a data-side
// decision made during ingest, so it is deliberately omitted here.
export const ALL_MARKET_CODES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'CH',
  'GB',
] as const;
export type AllMarketCode = (typeof ALL_MARKET_CODES)[number];

export const ALL_MARKET_NAMES: Record<AllMarketCode, string> = {
  AT: 'Austria',
  BE: 'Belgium',
  BG: 'Bulgaria',
  HR: 'Croatia',
  CZ: 'Czechia',
  DK: 'Denmark',
  EE: 'Estonia',
  FI: 'Finland',
  FR: 'France',
  DE: 'Germany',
  GR: 'Greece',
  HU: 'Hungary',
  IE: 'Ireland',
  IT: 'Italy',
  LV: 'Latvia',
  LT: 'Lithuania',
  NL: 'Netherlands',
  NO: 'Norway',
  PL: 'Poland',
  PT: 'Portugal',
  RO: 'Romania',
  SK: 'Slovakia',
  SI: 'Slovenia',
  ES: 'Spain',
  SE: 'Sweden',
  CH: 'Switzerland',
  GB: 'United Kingdom',
};

// Filing / web languages per market (ISO 639-1), used to route specialism
// discovery + web-evidence extraction. Multilingual markets list ALL of their
// languages (first = largest filing language). This is the source of truth the
// blueprint clinical-specialism synonyms (which are language-keyed, not
// market-keyed) resolve against — so a German term is authored once and reused
// across AT / DE / CH, and BE / CH / FI get every language they file in.
export const MARKET_LANGUAGES: Record<AllMarketCode, string[]> = {
  AT: ['de'],
  BE: ['nl', 'fr', 'de'],
  BG: ['bg'],
  HR: ['hr'],
  CZ: ['cs'],
  DK: ['da'],
  EE: ['et'],
  FI: ['fi', 'sv'],
  FR: ['fr'],
  DE: ['de'],
  GR: ['el'],
  HU: ['hu'],
  IE: ['en', 'ga'],
  IT: ['it'],
  LV: ['lv'],
  LT: ['lt'],
  NL: ['nl'],
  NO: ['nb'],
  PL: ['pl'],
  PT: ['pt'],
  RO: ['ro'],
  SK: ['sk'],
  SI: ['sl'],
  ES: ['es', 'ca', 'gl', 'eu'],
  SE: ['sv'],
  CH: ['de', 'fr', 'it'],
  GB: ['en', 'cy'],
};
